// SummaryEditor.jsx — Ren & kompakt, med lokalt edit-state för lagring

import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Collapse,
  Button,
  Tooltip,
  Popover,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import WorkspaceAvatarPicker from "../components/WorkspaceAvatarPicker";

/** ---- Storage steps and non-linear positions ------------------------------ */
const STORAGE_STEPS = [1, 2, 4, 10, 50, 100, 200, 300, 500, 1000];
const STORAGE_POS = [0, 6, 12, 24, 40, 55, 70, 82, 92, 100];

/** ---- UI helpers --------------------------------------------------------- */
function Section({ children, pb = 0 }) {
  return <Box sx={{ display: "block", pb }}>{children}</Box>;
}
function Row({ children, gap = 2, cols = "repeat(12, 1fr)" }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: cols }, gap }}>
      {children}
    </Box>
  );
}
function ListEditor({ label, items = [], placeholder = "Lägg till…", onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const next = Array.from(new Set([...(items || []), v]));
    onChange?.(next);
    setDraft("");
  };
  const remove = (i) => onChange?.(items.filter((_, idx) => idx !== i));
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          label={label}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Tooltip title="Lägg till">
          <span>
            <IconButton onClick={add} color="primary" disabled={!draft.trim()}>
              <AddRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {(items || []).map((m, i) => (
          <Chip
            key={`${m}-${i}`}
            label={m}
            onDelete={() => remove(i)}
            deleteIcon={<DeleteRoundedIcon />}
            sx={{ borderRadius: 999 }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

/** ---- Helpers: map mellan GB <-> slider-position ------------------------- */
function indexFromGB(gb) {
  const i = STORAGE_STEPS.indexOf(gb ?? STORAGE_STEPS[0]);
  return i >= 0 ? i : 0;
}
function posFromGB(gb) { return STORAGE_POS[indexFromGB(gb)]; }
function nearestIndexFromPos(pos) {
  let best = 0, bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < STORAGE_POS.length; i++) {
    const d = Math.abs(pos - STORAGE_POS[i]);
    if (d < bestDist) { best = i; bestDist = d; }
  }
  return best;
}

/** ---- Main ---------------------------------------------------------------- */
export default function SummaryEditor({ value, onPatch }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // init default storage om tomt
  useEffect(() => {
    if (value?.storageGB == null) {
      onPatch((d) => { d.storageGB = STORAGE_STEPS[0]; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lokalt edit-state för lagring när popover är öppen
  const [storageAnchor, setStorageAnchor] = useState(null);
  const [editingStorageGB, setEditingStorageGB] = useState(value?.storageGB ?? STORAGE_STEPS[0]);
  useEffect(() => {
    // när popover är stängd, synca lokalt värde från props
    if (!storageAnchor) setEditingStorageGB(value?.storageGB ?? STORAGE_STEPS[0]);
  }, [value?.storageGB, storageAnchor]);

  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("lg"));

  const storageLabel = useMemo(
    () => `${value?.storageGB ?? STORAGE_STEPS[0]} GB`,
    [value?.storageGB]
  );

  const openStorageEditor = (el) => {
    setEditingStorageGB(value?.storageGB ?? STORAGE_STEPS[0]);
    setStorageAnchor(el);
  };
  const closeStorageEditor = () => setStorageAnchor(null);

  return (
    <Stack spacing={3}>
      {/* GRUNDDATA */}
      <Section pb={1}>
        <Row cols="repeat(12, 1fr)" gap={2.25}>
          {/* Namn: enradigt, 53px */}
          <TextField
            label="Arbetsytans namn"
            value={value?.name || ""}
            onChange={(e) => onPatch((d) => { d.name = e.target.value; })}
            fullWidth
            sx={{ gridColumn: { md: "span 5" }, "& .MuiInputBase-root": { height: 53 } }}
          />

          {/* Beskrivning: enradigt, 53px */}
          <TextField
            label="Arbetsytans beskrivning"
            value={value?.description || ""}
            onChange={(e) => onPatch((d) => { d.description = e.target.value; })}
            fullWidth
            sx={{ gridColumn: { md: "span 5" }, "& .MuiInputBase-root": { height: 53 } }}
          />

          {/* Ikon/Avatar: 53x53 */}
          <Box sx={{ gridColumn: { md: "span 2" }, display: "flex", alignItems: "center" }}>
            <WorkspaceAvatarPicker
              value={{
                imageUrl: value?.imageUrl ?? null,
                iconId: value?.icon ?? null,
                color: value?.color ?? "#6a2135",
              }}
              onChange={(v) =>
                onPatch((d) => {
                  d.imageUrl = v.imageUrl ?? null;
                  d.icon = v.iconId ?? null;
                  d.color = v.color ?? d.color;
                })
              }
              noFrame
              compact
              hideHint
              height={53}
              width={53}
            />
          </Box>
        </Row>

        {/* Lagringsutrymme – text + penna; slider i Popover vid klick (lokalt edit-state) */}
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Arbetsytans lagringsutrymme är <b>{storageLabel}</b>
            </Typography>
            <IconButton
              aria-label="Ändra lagringsutrymme"
              size="small"
              onClick={(e) => openStorageEditor(e.currentTarget)}
              sx={{ ml: 0.5 }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Popover
            open={Boolean(storageAnchor)}
            anchorEl={storageAnchor}
            onClose={closeStorageEditor}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { px: 3, py: 3 } } }}  // lite högre + bredare
          >
            {(() => {
              const MARK_SET = compact ? [1, 2, 10, 100, 200, 1000] : STORAGE_STEPS;
              const marks = STORAGE_STEPS.map((gb, i) => ({
                value: STORAGE_POS[i],
                label: MARK_SET.includes(gb) ? String(gb) : "",
              }));

              // slider styrs av lokalt edit-state under drag
              const sliderPos = posFromGB(editingStorageGB);

              return (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ width: { xs: 420, sm: 560, md: 720 } }} // bredare
                >
                  <Slider
                    aria-label="Workspace storage"
                    value={sliderPos}
                    onChange={(_, pos) => {
                      const p = Array.isArray(pos) ? pos[0] : pos;
                      const next = STORAGE_STEPS[nearestIndexFromPos(p)];
                      setEditingStorageGB(next);
                    }}
                    onChangeCommitted={() => {
                      // committa först när användaren släpper
                      onPatch((d) => { d.storageGB = editingStorageGB; });
                      closeStorageEditor();
                    }}
                    min={0}
                    max={100}
                    step={null}
                    marks={marks}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(p) =>
                      `${STORAGE_STEPS[nearestIndexFromPos(Array.isArray(p) ? p[0] : p)]} GB`
                    }
                    getAriaValueText={(p) =>
                      `${STORAGE_STEPS[nearestIndexFromPos(Array.isArray(p) ? p[0] : p)]} GB`
                    }
                    sx={{
                      flexGrow: 1,
                      mt: 0.5,
                      "& .MuiSlider-markLabel": {
                        fontSize: 11,
                        transform: "translateY(6px)",
                        whiteSpace: "nowrap",
                        color: "text.secondary",
                      },
                      "& .MuiSlider-mark": { width: 2 },
                    }}
                  />
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 80, textAlign: "right" }}>
                    {editingStorageGB} GB
                  </Typography>
                </Stack>
              );
            })()}
          </Popover>
        </Box>
      </Section>

      {/* --- Klickbar Divider som fäller ut Avancerat --- */}
      <Divider sx={{ my: 1.5 }}>
        <Button
          size="small"
          onClick={() => setShowAdvanced((v) => !v)}
          endIcon={showAdvanced ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary", px: 1.25, py: 0.25 }}
        >
          Avancerat
        </Button>
      </Divider>

      {/* AVANCERAT */}
      <Collapse in={showAdvanced} unmountOnExit>
        <Section>
          <Stack spacing={2.25}>
            <Row cols="repeat(12, 1fr)" gap={2.25}>
              {/* Mall / Ägare */}
              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Arbetsytemall</InputLabel>
                <Select
                  label="Arbetsytemall"
                  value={value?.template || ""}
                  onChange={(e) => onPatch((d) => { d.template = e.target.value; })}
                >
                  <MenuItem value="">Ingen</MenuItem>
                  <MenuItem value="projekt">Projekt</MenuItem>
                  <MenuItem value="crm">CRM</MenuItem>
                  <MenuItem value="ärenden">Ärenden</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Arbetsyteägare</InputLabel>
                <Select
                  label="Arbetsyteägare"
                  value={value?.owner || ""}
                  onChange={(e) => onPatch((d) => { d.owner = e.target.value; })}
                >
                  <MenuItem value="">Välj…</MenuItem>
                  <MenuItem value="devops@kommunal.se">Devops Utvecklare</MenuItem>
                  <MenuItem value="you@company.se">Du</MenuItem>
                </Select>
              </FormControl>

              {/* Ordergrupp / Dashboard */}
              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Ordergrupp</InputLabel>
                <Select
                  label="Ordergrupp"
                  value={value?.orderGroup || ""}
                  onChange={(e) => onPatch((d) => { d.orderGroup = e.target.value; })}
                >
                  <MenuItem value="">Ingen</MenuItem>
                  <MenuItem value="alpha">Alpha</MenuItem>
                  <MenuItem value="beta">Beta</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Dashboard</InputLabel>
                <Select
                  label="Dashboard"
                  value={value?.dashboard || ""}
                  onChange={(e) => onPatch((d) => { d.dashboard = e.target.value; })}
                >
                  <MenuItem value="">Standard</MenuItem>
                  <MenuItem value="light">Light dashboard</MenuItem>
                  <MenuItem value="pro">Pro dashboard</MenuItem>
                </Select>
              </FormControl>

              {/* Nätverk / Startsida */}
              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Nätverk</InputLabel>
                <Select
                  label="Nätverk"
                  value={value?.network || "Wilma network"}
                  onChange={(e) => onPatch((d) => { d.network = e.target.value; })}
                >
                  <MenuItem value="Wilma network">Wilma network</MenuItem>
                  <MenuItem value="Extern">Extern</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Startsida</InputLabel>
                <Select
                  label="Startsida"
                  value={value?.startPage || ""}
                  onChange={(e) => onPatch((d) => { d.startPage = e.target.value; })}
                >
                  <MenuItem value="">Översikt</MenuItem>
                  <MenuItem value="feed">Nyhetsflöde</MenuItem>
                  <MenuItem value="dashboard">Dashboard</MenuItem>
                </Select>
              </FormControl>
            </Row>

            {/* Menyer / Roller */}
            <Row cols="repeat(12, 1fr)" gap={2.25}>
              <Box sx={{ gridColumn: { md: "span 6" } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Menyer
                </Typography>
                <ListEditor
                  label="Ny meny"
                  items={value?.menus || []}
                  onChange={(list) => onPatch((d) => { d.menus = list; })}
                />
              </Box>

              <Box sx={{ gridColumn: { md: "span 6" } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Roller
                </Typography>
                <ListEditor
                  label="Ny roll"
                  items={value?.roles || []}
                  onChange={(list) => onPatch((d) => { d.roles = list; })}
                />
              </Box>
            </Row>
          </Stack>
        </Section>
      </Collapse>
    </Stack>
  );
}
